import { isMorphoBreakdown } from './isMorphoBreakdown'

type RawAmount = string

export interface MerklRewardAmounts {
  amount: RawAmount
  claimed: RawAmount
  pending: RawAmount
  breakdowns: Array<{
    opportunity: Parameters<typeof isMorphoBreakdown>[0]
    amount: RawAmount
    claimed: RawAmount
    pending: RawAmount
  }>
}

/**
 * Compute Morpho-attributed claimable and accruing amounts for a Merkl reward.
 *
 * The on-chain merkle root (`reward.amount - reward.claimed`) is the upper bound:
 * `Σ breakdown.amount` can exceed `reward.amount` when Merkl finalizes a root that
 * excludes off-chain attribution, so we apportion by Morpho's share of the unclaimed
 * breakdown amount (`b.amount - b.claimed`).
 *
 * Merkl docs note breakdowns "may be incomplete and not show all campaigns", so we
 * also cap by `morphoUnclaimed` / `morphoBreakdownPending` — the sum of what Merkl
 * itself attributes to Morpho campaigns. This prevents over-reporting Morpho when
 * non-Morpho breakdowns are missing (otherwise `morpho/total` would inflate to 1).
 */
export const calculateMorphoClaimable = (
  reward: MerklRewardAmounts
): { claimable: bigint; accruing: bigint } => {
  const morphoBreakdowns = reward.breakdowns.filter(b => isMorphoBreakdown(b.opportunity))
  if (morphoBreakdowns.length === 0) return { claimable: 0n, accruing: 0n }

  const totalClaimed = BigInt(reward.claimed)
  const totalPending = BigInt(reward.pending)

  const onchainClaimable =
    BigInt(reward.amount) > totalClaimed ? BigInt(reward.amount) - totalClaimed : 0n

  const breakdownUnclaimed = (b: { amount: string; claimed: string }) => {
    const amount = BigInt(b.amount)
    const claimed = BigInt(b.claimed)
    return amount > claimed ? amount - claimed : 0n
  }

  const totalUnclaimed = reward.breakdowns.reduce((sum, b) => sum + breakdownUnclaimed(b), 0n)
  const morphoUnclaimed = morphoBreakdowns.reduce((sum, b) => sum + breakdownUnclaimed(b), 0n)
  const totalBreakdownPending = reward.breakdowns.reduce((sum, b) => sum + BigInt(b.pending), 0n)
  const morphoBreakdownPending = morphoBreakdowns.reduce((sum, b) => sum + BigInt(b.pending), 0n)

  const apportionedClaimable =
    totalUnclaimed > 0n ? (onchainClaimable * morphoUnclaimed) / totalUnclaimed : 0n
  const claimable = apportionedClaimable < morphoUnclaimed ? apportionedClaimable : morphoUnclaimed

  const apportionedAccruing =
    totalBreakdownPending > 0n
      ? (totalPending * morphoBreakdownPending) / totalBreakdownPending
      : 0n
  const accruing =
    apportionedAccruing < morphoBreakdownPending ? apportionedAccruing : morphoBreakdownPending

  return { claimable, accruing }
}
